import { Component, ElementRef, OnInit } from '@angular/core'
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { combineLatest, Observable, of } from 'rxjs'
import { catchError, map, shareReplay, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { Tag } from 'src/app/features/tag/tag'
import { tagActions } from 'src/app/features/tag/tag.actions'
import { tagSelectors } from 'src/app/features/tag/tag.selectors'
import {
  DiscoveryAreaConfigWebOption,
  DiscoveryAreaConfigType,
  DiscoveryAreaItemType,
  DiscoveryAreaUpliftedItem,
  DiscoveryTagGroup,
  DiscoveryUpliftedArea
} from 'src/app/shared/discovery-area.types'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'
import { ConfigOptionsKeys } from '../../../shared/constants'
import {
  normalizeNumber,
  normalizeNumberList
} from '../../../shared/utils/numbers'
import { environment } from 'src/environments/environment'

type LanguageCode = 'nl' | 'en' | 'de' | 'fr'
type TitleKey = `title_${LanguageCode}`
type TitleSource = Partial<Record<TitleKey, string | null | undefined>>

@Component({
  selector: 'configuration-option-discovery-area-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionDiscoveryAreaTabComponent implements OnInit {
  public form: UntypedFormGroup
  public readonly editAccessRights = [
    AccessRights.Settings_write,
    AccessRights.ConfigurationOptions_write
  ]
  public readonly itemTypes = DiscoveryAreaItemType
  public tags$: Observable<Tag[]>
  public configCollapsed: boolean[] = []
  public upliftedAreaCollapsed: boolean[][] = []
  public upliftedItemCollapsed: boolean[][][] = []
  public tagGroupCollapsed: boolean[][] = []
  public readonly languageCodes: LanguageCode[] = ['nl', 'en', 'de', 'fr']
  public readonly showOverviewSection =
    environment.moduleConfigs.configOptionModule.showOverviewSection ?? false
  public readonly configTypeOptions = [
    {
      value: DiscoveryAreaConfigType.COURSE_DISCOVERY,
      label: 'configurationOptionModule.discoveryAreaType1'
    },
    {
      value: DiscoveryAreaConfigType.RENTAL_DISCOVERY,
      label: 'configurationOptionModule.discoveryAreaType2'
    },
    {
      value: DiscoveryAreaConfigType.ACTIVITY_DISCOVERY,
      label: 'configurationOptionModule.discoveryAreaType3'
    },
    ...(this.showOverviewSection
      ? [
          {
            value: DiscoveryAreaConfigType.OVERVIEW_DISCOVERY,
            label: 'configurationOptionModule.discoveryAreaType4'
          }
        ]
      : [])
  ]
  public readonly itemTypeOptions = [
    {
      value: DiscoveryAreaItemType.TAG,
      label: 'configurationOptionModule.discoveryItemTypeTag'
    },
    {
      value: DiscoveryAreaItemType.LINK,
      label: 'configurationOptionModule.discoveryItemTypeLink'
    }
  ]
  public levelOptions$: Observable<{ label: string; value: number }[]>

  private readonly upliftedItemValidator = (group: UntypedFormGroup) => {
    const type = group.get('type')?.value
    const id = normalizeNumber(group.get('id')?.value)
    const url = String(group.get('url')?.value ?? '').trim()

    if (type === DiscoveryAreaItemType.TAG && id === null) {
      return { itemIdRequired: true }
    }

    if (type === DiscoveryAreaItemType.LINK && !url) {
      return { urlRequired: true }
    }

    return null
  }

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configOptionService: ConfigurationOptionService,
    private notificationService: NotificationService,
    private actions$: Actions,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      configs: this.fb.array([])
    })

    this.tags$ = combineLatest([
      this.store.select(tagSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ]).pipe(
      map(([tags, language]) => {
        const languageCode = language ?? 'en'
        const activeTags = tags.filter((tag) => tag.activeState)
        return activeTags.map((tag) => ({
          ...tag,
          description: tag[`description_${languageCode}`] ?? ''
        }))
      }),
      shareReplay(1)
    )

    this.levelOptions$ = this.tags$.pipe(
      map((tags) => {
        const levels = new Set<number>()

        tags.forEach((tag) => {
          const level = normalizeNumber(tag.level)
          if (level !== null) {
            levels.add(level)
          }
        })

        return [...levels]
          .sort((a, b) => a - b)
          .map((level) => ({
            value: level,
            label: `${level}`
          }))
      })
    )

    this.store.dispatch(tagActions.fetchMany({}))

    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<
          DiscoveryAreaConfigWebOption[]
        >(ConfigOptionsKeys.DISCOVERY_AREA_CONFIG)
      )
      .pipe(take(1))
      .subscribe((discoveryAreaConfigurations) => {
        if (!discoveryAreaConfigurations) {
          return
        }
        discoveryAreaConfigurations.forEach((config) => this.addConfig(config))
      })
  }

  get configs(): UntypedFormArray {
    return this.form.get('configs') as UntypedFormArray
  }

  public getConfigTypeOptions(index: number) {
    const usedTypes = this.configs.controls
      .map((group, groupIndex) =>
        groupIndex === index ? null : group.get('type')?.value
      )
      .filter((value) => value !== null && value !== undefined)

    return this.configTypeOptions.map((option) => ({
      ...option,
      disabled: usedTypes.includes(option.value)
    }))
  }

  public canAddConfig(): boolean {
    const visibleConfigCount = this.configs.controls.filter((config) =>
      this.isConfigVisible(config)
    ).length

    return visibleConfigCount < this.configTypeOptions.length
  }

  public isConfigVisible(config: AbstractControl): boolean {
    return (
      this.showOverviewSection ||
      config.get('type')?.value !== DiscoveryAreaConfigType.OVERVIEW_DISCOVERY
    )
  }

  public getConfigTypeLabel(typeValue: number | null | undefined): string {
    const option = this.configTypeOptions.find(
      (configOption) => configOption.value === typeValue
    )
    return option?.label ?? 'general.select'
  }

  public addConfig(config?: DiscoveryAreaConfigWebOption): void {
    if (!config && !this.canAddConfig()) {
      return
    }

    const shouldCollapse = Boolean(config)
    this.configCollapsed.push(shouldCollapse)
    this.initializeConfigCollapseState(config)
    this.configs.push(this.createConfigGroup(config))
  }

  public removeConfig(index: number): void {
    this.configs.removeAt(index)
    this.configCollapsed.splice(index, 1)
    this.upliftedAreaCollapsed.splice(index, 1)
    this.upliftedItemCollapsed.splice(index, 1)
    this.tagGroupCollapsed.splice(index, 1)
  }

  public addUpliftedAreaSet(
    configIndex: number,
    areaSet?: DiscoveryUpliftedArea
  ): void {
    this.getUpliftedAreaSets(configIndex).push(
      this.createUpliftedAreaSetGroup(areaSet)
    )
    this.upliftedAreaCollapsed[configIndex] =
      this.upliftedAreaCollapsed[configIndex] ?? []
    this.upliftedItemCollapsed[configIndex] =
      this.upliftedItemCollapsed[configIndex] ?? []

    const shouldCollapse = Boolean(areaSet)
    const itemsCollapsed = (areaSet?.items ?? []).map(() => true)

    this.upliftedAreaCollapsed[configIndex].push(shouldCollapse)
    this.upliftedItemCollapsed[configIndex].push(itemsCollapsed)
  }

  public removeUpliftedAreaSet(configIndex: number, setIndex: number): void {
    this.getUpliftedAreaSets(configIndex).removeAt(setIndex)
    this.upliftedAreaCollapsed[configIndex]?.splice(setIndex, 1)
    this.upliftedItemCollapsed[configIndex]?.splice(setIndex, 1)
  }

  public addUpliftedItem(
    configIndex: number,
    setIndex: number,
    item?: DiscoveryAreaUpliftedItem
  ): void {
    this.getUpliftedItems(configIndex, setIndex).push(
      this.createUpliftedItemGroup(item)
    )
    this.upliftedItemCollapsed[configIndex] =
      this.upliftedItemCollapsed[configIndex] ?? []
    this.upliftedItemCollapsed[configIndex][setIndex] =
      this.upliftedItemCollapsed[configIndex][setIndex] ?? []
    this.upliftedItemCollapsed[configIndex][setIndex].push(Boolean(item))
  }

  public removeUpliftedItem(
    configIndex: number,
    setIndex: number,
    itemIndex: number
  ): void {
    this.getUpliftedItems(configIndex, setIndex).removeAt(itemIndex)
    this.upliftedItemCollapsed[configIndex]?.[setIndex]?.splice(itemIndex, 1)
  }

  public addTagGroup(configIndex: number, tagGroup?: DiscoveryTagGroup): void {
    this.getTagGroups(configIndex).push(this.createTagGroup(tagGroup))
    this.tagGroupCollapsed[configIndex] =
      this.tagGroupCollapsed[configIndex] ?? []
    this.tagGroupCollapsed[configIndex].push(Boolean(tagGroup))
  }

  public removeTagGroup(configIndex: number, tagIndex: number): void {
    this.getTagGroups(configIndex).removeAt(tagIndex)
    this.tagGroupCollapsed[configIndex]?.splice(tagIndex, 1)
  }

  public getUpliftedAreaSets(configIndex: number): UntypedFormArray {
    return this.configs
      .at(configIndex)
      .get('upliftedAreaSets') as UntypedFormArray
  }

  public getUpliftedItems(
    configIndex: number,
    setIndex: number
  ): UntypedFormArray {
    return this.getUpliftedAreaSets(configIndex)
      .at(setIndex)
      .get('items') as UntypedFormArray
  }

  public getTagGroups(configIndex: number): UntypedFormArray {
    return this.configs.at(configIndex).get('tagGroups') as UntypedFormArray
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      this.markDirty(this.form)
      this.expandInvalidPanels()
      setTimeout(() => this.scrollToFirstInvalidControl())
      return
    }

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )

    this.configOptionService.updateOrCreateMany({
      [ConfigOptionsKeys.DISCOVERY_AREA_CONFIG]: this.buildPayload()
    })
  }

  private createConfigGroup(
    config?: DiscoveryAreaConfigWebOption
  ): UntypedFormGroup {
    return this.fb.group({
      type: [config?.type ?? null, [Validators.required]],
      ...this.buildTitleControls(config),
      upliftedAreaSets: this.fb.array(
        (config?.upliftedAreaSets ?? []).map((areaSet) =>
          this.createUpliftedAreaSetGroup(areaSet)
        )
      ),
      tagGroups: this.fb.array(
        (config?.tagGroups ?? []).map((tagGroup) =>
          this.createTagGroup(tagGroup)
        )
      )
    })
  }

  private createUpliftedAreaSetGroup(
    areaSet?: DiscoveryUpliftedArea
  ): UntypedFormGroup {
    return this.fb.group({
      ...this.buildTitleControls(areaSet),
      items: this.fb.array(
        (areaSet?.items ?? []).map((item) => this.createUpliftedItemGroup(item))
      )
    })
  }

  private createUpliftedItemGroup(
    item?: DiscoveryAreaUpliftedItem
  ): UntypedFormGroup {
    return this.fb.group(
      {
        type: [item?.type ?? DiscoveryAreaItemType.TAG, [Validators.required]],
        imageUrl: [item?.imageUrl ?? null, [Validators.required]],
        id: [item?.id ?? null, []],
        url: [item?.url ?? null, []],
        ...this.buildTitleControls(item)
      },
      { validators: [this.upliftedItemValidator] }
    )
  }

  private createTagGroup(tagGroup?: DiscoveryTagGroup): UntypedFormGroup {
    return this.fb.group({
      ...this.buildTitleControls(tagGroup),
      levels: [normalizeNumberList(tagGroup?.levels), []],
      targetTagIds: [normalizeNumberList(tagGroup?.targetTagIds), []]
    })
  }

  private buildPayload(): DiscoveryAreaConfigWebOption[] {
    const rawConfigs = (this.form.getRawValue().configs ?? []) as Array<any>

    return rawConfigs.map((config) => ({
      type: (normalizeNumber(config.type) ??
        DiscoveryAreaConfigType.COURSE_DISCOVERY) as DiscoveryAreaConfigType,
      ...this.buildRequiredTitles(config),
      upliftedAreaSets: (config.upliftedAreaSets ?? []).map((areaSet) => ({
        ...this.buildRequiredTitles(areaSet),
        items: (areaSet.items ?? []).map((item) =>
          this.normalizeUpliftedItem(item)
        )
      })),
      tagGroups: (config.tagGroups ?? []).map((tagGroup) => ({
        ...this.buildRequiredTitles(tagGroup),
        levels: normalizeNumberList(tagGroup.levels),
        targetTagIds: normalizeNumberList(tagGroup.targetTagIds)
      }))
    }))
  }

  private normalizeUpliftedItem(item: any): DiscoveryAreaUpliftedItem {
    const type = normalizeNumber(item.type) ?? DiscoveryAreaItemType.TAG
    const id = normalizeNumber(item.id)
    const url = item?.url ? String(item.url).trim() : undefined
    const isLink = type === DiscoveryAreaItemType.LINK

    return {
      type: type as DiscoveryAreaItemType,
      imageUrl: item?.imageUrl ?? '',
      ...(isLink ? this.buildOptionalTitles(item) : {}),
      id: !isLink ? (id ?? undefined) : undefined,
      url: isLink ? url || undefined : undefined
    }
  }

  private initializeConfigCollapseState(
    config?: DiscoveryAreaConfigWebOption
  ): void {
    const areaSets = config?.upliftedAreaSets ?? []
    const tagGroups = config?.tagGroups ?? []

    this.upliftedAreaCollapsed.push(areaSets.map(() => true))
    this.upliftedItemCollapsed.push(
      areaSets.map((areaSet) => (areaSet.items ?? []).map(() => true))
    )
    this.tagGroupCollapsed.push(tagGroups.map(() => true))
  }

  private expandInvalidPanels(): void {
    this.configs.controls.forEach((config, configIndex) => {
      if (config.invalid) {
        this.configCollapsed[configIndex] = false
      }

      const upliftedAreaSets = config.get(
        'upliftedAreaSets'
      ) as UntypedFormArray

      upliftedAreaSets.controls.forEach((areaSet, areaSetIndex) => {
        if (areaSet.invalid) {
          this.upliftedAreaCollapsed[configIndex] =
            this.upliftedAreaCollapsed[configIndex] ?? []
          this.upliftedAreaCollapsed[configIndex][areaSetIndex] = false
        }

        const items = areaSet.get('items') as UntypedFormArray

        items.controls.forEach((item, itemIndex) => {
          if (item.invalid) {
            this.upliftedItemCollapsed[configIndex] =
              this.upliftedItemCollapsed[configIndex] ?? []
            this.upliftedItemCollapsed[configIndex][areaSetIndex] =
              this.upliftedItemCollapsed[configIndex][areaSetIndex] ?? []
            this.upliftedItemCollapsed[configIndex][areaSetIndex][itemIndex] =
              false
          }
        })
      })

      const tagGroups = config.get('tagGroups') as UntypedFormArray

      tagGroups.controls.forEach((tagGroup, tagGroupIndex) => {
        if (tagGroup.invalid) {
          this.tagGroupCollapsed[configIndex] =
            this.tagGroupCollapsed[configIndex] ?? []
          this.tagGroupCollapsed[configIndex][tagGroupIndex] = false
        }
      })
    })
  }

  private scrollToFirstInvalidControl(): void {
    const id = this.getFirstInvalidControlId()
    if (!id) {
      return
    }

    const host = this.elementRef.nativeElement
    const target =
      host.ownerDocument.getElementById(id) ??
      host.querySelector<HTMLElement>(`label[for="${id}"]`)

    if (!target) {
      return
    }

    const scrollTarget =
      target.closest<HTMLElement>('.mb-4, .mb-3, .col-lg-4') ?? target

    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

    if ('focus' in target) {
      target.focus()
    }
  }

  private getFirstInvalidControlId(): string | null {
    for (
      let configIndex = this.configs.length - 1;
      configIndex >= 0;
      configIndex--
    ) {
      const config = this.configs.at(configIndex)
      const upliftedAreaSets = config.get(
        'upliftedAreaSets'
      ) as UntypedFormArray

      for (
        let areaSetIndex = upliftedAreaSets.length - 1;
        areaSetIndex >= 0;
        areaSetIndex--
      ) {
        const items = upliftedAreaSets
          .at(areaSetIndex)
          .get('items') as UntypedFormArray

        for (let itemIndex = items.length - 1; itemIndex >= 0; itemIndex--) {
          const item = items.at(itemIndex)
          const itemId = `${configIndex}-${areaSetIndex}-${itemIndex}`

          if (item.get('type')?.value === DiscoveryAreaItemType.LINK) {
            if (item.errors?.urlRequired) {
              return `uplifted-item-url-${itemId}`
            }
          } else if (item.errors?.itemIdRequired) {
            return `uplifted-item-id-${itemId}`
          }

          if (item.get('imageUrl')?.invalid) {
            return `uplifted-item-image-url-${itemId}`
          }

          if (item.get('type')?.invalid) {
            return `uplifted-item-type-${itemId}`
          }
        }
      }

      if (config.get('type')?.invalid) {
        return `config-type-${configIndex}`
      }
    }

    return null
  }

  private buildTitleControls(
    source: TitleSource | null | undefined
  ): Record<TitleKey, any> {
    return this.languageCodes.reduce(
      (controls, language) => {
        const key = this.getTitleKey(language)
        controls[key] = [source?.[key] ?? null, []]
        return controls
      },
      {} as Record<TitleKey, any>
    )
  }

  private buildRequiredTitles(
    source: TitleSource | null | undefined
  ): Record<TitleKey, string> {
    return this.languageCodes.reduce(
      (titles, language) => {
        const key = this.getTitleKey(language)
        titles[key] = source?.[key] ?? ''
        return titles
      },
      {} as Record<TitleKey, string>
    )
  }

  private buildOptionalTitles(
    source: TitleSource | null | undefined
  ): Partial<Record<TitleKey, string>> {
    return this.languageCodes.reduce(
      (titles, language) => {
        const key = this.getTitleKey(language)
        const value = source?.[key]
        if (value) {
          titles[key] = String(value)
        }
        return titles
      },
      {} as Partial<Record<TitleKey, string>>
    )
  }

  private getTitleKey(language: LanguageCode): TitleKey {
    return `title_${language}` as TitleKey
  }

  private markDirty(control: AbstractControl): void {
    if (control instanceof UntypedFormControl) {
      control.markAsDirty()
      control.markAsTouched()
      return
    }

    if (control instanceof UntypedFormArray) {
      control.markAsDirty()
      control.markAsTouched()
      control.controls.forEach((child) => this.markDirty(child))
      return
    }

    if (control instanceof UntypedFormGroup) {
      control.markAsDirty()
      control.markAsTouched()
      Object.values(control.controls).forEach((child) => this.markDirty(child))
    }
  }
}
